import { getAddress } from "viem";

import type { MarketsInfoData } from "types/markets";
import type { TradeAction as SubsquidTradeAction } from "types/subsquid";

/**
 * Subsquid API may return transaction { hash } or transactionHash; normalize to { hash }.
 * Both shapes are declared optional here because the generated `TradeAction` only carries one of
 * them, so reading the other would otherwise not type-check.
 */
function getTransaction(
  raw: SubsquidTradeAction & { transaction?: { hash?: string }; transactionHash?: string }
): { hash: string } {
  const hash = raw.transaction?.hash ?? raw.transactionHash ?? "";
  return { hash };
}
import { Token, TokensData } from "types/tokens";
import type { PositionTradeAction } from "types/tradeHistory";
import type { SwapTradeAction } from "types/tradeHistory";

import { getByKey } from "./objects";
import { isDecreaseOrderType, isIncreaseOrderType, isLiquidationOrderType, isSwapOrderType } from "./orders";
import { getSwapPathOutputAddresses } from "./swap/swapStats";
import { convertToUsd, parseContractPrice } from "./tokens";
import { TradeActionType } from "../types/tradeHistory";

export function createRawTradeActionTransformer(
  marketsInfoData: MarketsInfoData,
  wrappedToken: Token,
  tokensData: TokensData
): (
  value: SubsquidTradeAction,
  index: number,
  array: SubsquidTradeAction[]
) => SwapTradeAction | PositionTradeAction | undefined {
  return (rawAction) => {
    const orderType = Number(rawAction.orderType);

    if (isSwapOrderType(orderType)) {
      const initialCollateralTokenAddress = getAddress(rawAction.initialCollateralTokenAddress!);
      const swapPath = rawAction.swapPath!.map((address) => getAddress(address));

      const swapPathOutputAddresses = getSwapPathOutputAddresses({
        marketsInfoData,
        swapPath,
        initialCollateralAddress: initialCollateralTokenAddress,
        wrappedNativeTokenAddress: wrappedToken.address,
        shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
        isIncrease: false,
      });

      const initialCollateralToken = getByKey(tokensData, initialCollateralTokenAddress)!;
      const targetCollateralToken = getByKey(tokensData, swapPathOutputAddresses.outTokenAddress)!;

      if (!initialCollateralToken || !targetCollateralToken) {
        return undefined;
      }

      const tradeAction: SwapTradeAction = {
        type: "swap",
        id: rawAction.id,
        srcChainId: rawAction.srcChainId ? Number(rawAction.srcChainId) : undefined,
        eventName: rawAction.eventName as TradeActionType,
        account: rawAction.account,
        swapPath,
        orderType,
        orderKey: rawAction.orderKey,
        initialCollateralTokenAddress: rawAction.initialCollateralTokenAddress!,
        initialCollateralDeltaAmount: bigNumberify(rawAction.initialCollateralDeltaAmount)!,
        minOutputAmount: bigNumberify(rawAction.minOutputAmount)!,
        executionAmountOut: rawAction.executionAmountOut ? bigNumberify(rawAction.executionAmountOut) : undefined,
        shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
        targetCollateralToken,
        initialCollateralToken,
        timestamp: rawAction.timestamp,
        transaction: getTransaction(rawAction as SubsquidTradeAction & { transactionHash?: string }),
        reason: rawAction.reason ?? undefined,
        reasonBytes: rawAction.reasonBytes ?? undefined,
        twapParams:
          rawAction.twapGroupId && rawAction.numberOfParts
            ? {
                twapGroupId: rawAction.twapGroupId,
                numberOfParts: rawAction.numberOfParts,
              }
            : undefined,
      };

      return tradeAction;
    } else {
      const marketAddress = getAddress(rawAction.marketAddress!);
      const marketInfo = getByKey(marketsInfoData, marketAddress);
      const indexToken = marketInfo?.indexToken;
      const initialCollateralTokenAddress = getAddress(rawAction.initialCollateralTokenAddress!);
      const swapPath = rawAction.swapPath!.map((address) => getAddress(address));
      const swapPathOutputAddresses = getSwapPathOutputAddresses({
        marketsInfoData,
        swapPath,
        initialCollateralAddress: initialCollateralTokenAddress,
        wrappedNativeTokenAddress: wrappedToken.address,
        shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
        isIncrease: isIncreaseOrderType(rawAction.orderType),
      });
      const initialCollateralToken = getByKey(tokensData, initialCollateralTokenAddress);
      const targetCollateralToken = getByKey(tokensData, swapPathOutputAddresses.outTokenAddress);

      if (!marketInfo || !indexToken || !initialCollateralToken || !targetCollateralToken) {
        return undefined;
      }

      const tradeAction: PositionTradeAction = {
        type: "position",
        id: rawAction.id,
        eventName: rawAction.eventName as TradeActionType,
        account: rawAction.account,
        marketAddress,
        marketInfo,
        srcChainId: rawAction.srcChainId ? Number(rawAction.srcChainId) : undefined,
        indexToken,
        swapPath,
        initialCollateralTokenAddress,
        initialCollateralToken,
        targetCollateralToken,
        initialCollateralDeltaAmount: bigNumberify(rawAction.initialCollateralDeltaAmount)!,
        sizeDeltaUsd: bigNumberify(rawAction.sizeDeltaUsd)!,
        sizeDeltaInTokens: rawAction.sizeDeltaInTokens ? bigNumberify(rawAction.sizeDeltaInTokens) : undefined,
        triggerPrice: rawAction.triggerPrice
          ? parseContractPrice(bigNumberify(rawAction.triggerPrice)!, indexToken.decimals)
          : undefined,
        acceptablePrice: parseContractPrice(bigNumberify(rawAction.acceptablePrice)!, indexToken.decimals),
        executionPrice: rawAction.executionPrice
          ? parseContractPrice(bigNumberify(rawAction.executionPrice)!, indexToken.decimals)
          : undefined,
        minOutputAmount: bigNumberify(rawAction.minOutputAmount)!,

        collateralTokenPriceMax: rawAction.collateralTokenPriceMax
          ? parseContractPrice(bigNumberify(rawAction.collateralTokenPriceMax)!, initialCollateralToken.decimals)
          : undefined,

        collateralTokenPriceMin: rawAction.collateralTokenPriceMin
          ? parseContractPrice(bigNumberify(rawAction.collateralTokenPriceMin)!, initialCollateralToken.decimals)
          : undefined,

        indexTokenPriceMin: rawAction.indexTokenPriceMin
          ? parseContractPrice(BigInt(rawAction.indexTokenPriceMin), indexToken.decimals)
          : undefined,
        indexTokenPriceMax: rawAction.indexTokenPriceMax
          ? parseContractPrice(BigInt(rawAction.indexTokenPriceMax), indexToken.decimals)
          : undefined,

        orderType,
        orderKey: rawAction.orderKey,
        isLong: rawAction.isLong!,
        basePnlUsd: rawAction.basePnlUsd ? BigInt(rawAction.basePnlUsd) : undefined,

        priceImpactDiffUsd: rawAction.priceImpactDiffUsd ? BigInt(rawAction.priceImpactDiffUsd) : undefined,
        priceImpactUsd: rawAction.priceImpactUsd ? BigInt(rawAction.priceImpactUsd) : undefined,
        totalImpactUsd: rawAction.totalImpactUsd ? BigInt(rawAction.totalImpactUsd) : undefined,
        positionFeeAmount: rawAction.positionFeeAmount ? BigInt(rawAction.positionFeeAmount) : undefined,
        borrowingFeeAmount: rawAction.borrowingFeeAmount ? BigInt(rawAction.borrowingFeeAmount) : undefined,
        fundingFeeAmount: rawAction.fundingFeeAmount ? BigInt(rawAction.fundingFeeAmount) : undefined,
        liquidationFeeAmount: rawAction.liquidationFeeAmount ? BigInt(rawAction.liquidationFeeAmount) : undefined,

        reason: rawAction.reason ?? undefined,
        reasonBytes: rawAction.reasonBytes ?? undefined,

        transaction: getTransaction(rawAction as SubsquidTradeAction & { transactionHash?: string }),
        timestamp: rawAction.timestamp,
        shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
        twapParams:
          rawAction.twapGroupId && rawAction.numberOfParts
            ? {
                twapGroupId: rawAction.twapGroupId,
                numberOfParts: rawAction.numberOfParts,
              }
            : undefined,
      };

      tradeAction.pnlUsd = getTradeActionPnlUsd(tradeAction);

      return tradeAction;
    }
  };
}

export function getTradeActionPnlUsd({
  basePnlUsd,
  borrowingFeeAmount,
  fundingFeeAmount,
  positionFeeAmount,
  liquidationFeeAmount,
  collateralTokenPriceMax,
  initialCollateralToken,
  totalImpactUsd = 0n,
  orderType,
}: PositionTradeAction): bigint | undefined {
  if (
    (!isDecreaseOrderType(orderType) && !isLiquidationOrderType(orderType)) ||
    basePnlUsd === undefined ||
    borrowingFeeAmount === undefined ||
    fundingFeeAmount === undefined ||
    positionFeeAmount === undefined ||
    liquidationFeeAmount === undefined ||
    collateralTokenPriceMax === undefined ||
    initialCollateralToken === undefined
  ) {
    return undefined;
  }

  const feeTotalAmount = borrowingFeeAmount + fundingFeeAmount + positionFeeAmount + liquidationFeeAmount;
  const feeUsd = convertToUsd(feeTotalAmount, initialCollateralToken.decimals, collateralTokenPriceMax)!;

  return basePnlUsd - feeUsd + totalImpactUsd;
}

export function bigNumberify(n?: bigint | string | null | undefined) {
  try {
    if (n === undefined) throw new Error("n is undefined");
    if (n === null) throw new Error("n is null");

    return BigInt(n);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("bigNumberify error", e);
    return undefined;
  }
}
